import {
  appBootstrapV2QueryOptions,
  bootstrapToAppConfig as commonBootstrapToAppConfig,
  bootstrapToBrandAppConfig as commonBootstrapToBrandAppConfig,
  getBootstrapShared,
  parseLegacyDtcThemeResponse,
  ServerThemeType,
} from '@brandclub/common-ui';
import { AppConfig, BrandAppConfig } from './redux/types';
import { getAppThemeV1, getAppConfigV1, getBrandAppConfigsV1 } from './api/rest/AppConfig';
import { queryClient } from './api/cache/queryClient';

// Local AppConfig/BrandAppConfig types are structurally compatible with the
// common-ui versions used by the shared adapters (`brandclub-checkout-shop-app`
// has its own local re-typing). Cast at the boundary to keep the local
// type as the public shape in this package.
const bootstrapToAppConfig = commonBootstrapToAppConfig as unknown as (
  b: Awaited<ReturnType<typeof getBootstrapShared>>,
) => AppConfig;
const bootstrapToBrandAppConfig = commonBootstrapToBrandAppConfig as unknown as (
  b: Awaited<ReturnType<typeof getBootstrapShared>>,
) => BrandAppConfig;

/**
 * Fires exactly one `GET /brandapp-v2/bootstrap` per page session.
 * `getBootstrapShared` dedupes in-flight calls but clears on settle;
 * the memo here persists the settled promise so all consumers share
 * the same result without re-hitting the endpoint.
 *
 * `withBootstrap` owns the V2-first / V1-fallback pattern so callers
 * declare their two data sources without any branching logic of their own.
 */
export type BootstrapResult = Awaited<ReturnType<typeof getBootstrapShared>>;
let bootstrapMemo: Promise<BootstrapResult> | null = null;

function getBootstrapOnce(): Promise<BootstrapResult> {
  if (!bootstrapMemo) {
    bootstrapMemo = getBootstrapShared().then((result) => {
      // AppInitializer blocks children until this resolves, then ConciergeButton
      // calls `useAppBootstrapV2` (different query key than BrandAppConfigs).
      // Seed that cache so the launcher does not refetch `/bootstrap`.
      queryClient.setQueryData(appBootstrapV2QueryOptions().queryKey, result);
      return result;
    });
  }
  return bootstrapMemo;
}

async function getBootstrap(): Promise<BootstrapResult | null> {
  try {
    return await getBootstrapOnce();
  } catch {
    return null;
  }
}

async function withBootstrap<T>(
  fromBootstrap: (result: BootstrapResult) => T | null | undefined,
  fromV1: () => Promise<T>,
): Promise<T> {
  const bootstrap = await getBootstrap();
  if (bootstrap !== null) {
    const result = fromBootstrap(bootstrap);
    if (result != null) return result;
  }
  return fromV1();
}

export const getAppTheme = (signal?: AbortSignal): Promise<ServerThemeType> =>
  withBootstrap(
    (bootstrap) =>
      parseLegacyDtcThemeResponse(bootstrap.themes?.dtc?.data as ServerThemeType),
    () => getAppThemeV1(signal),
  );

export const getAppConfig = (signal?: AbortSignal): Promise<AppConfig> =>
  withBootstrap(
    (bootstrap) => bootstrapToAppConfig(bootstrap),
    () => getAppConfigV1(signal),
  );

export const getBrandAppConfigs = (signal?: AbortSignal): Promise<BrandAppConfig> =>
  withBootstrap(
    (bootstrap) => bootstrapToBrandAppConfig(bootstrap),
    () => getBrandAppConfigsV1(signal),
  );
